<template>
  <div class="pay-page">
    <!-- ═══ APPBAR ═══ -->
    <header class="appbar">
      <router-link to="/" class="vm-mark">
        <img
          class="vm-logo"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </router-link>
      <nav class="appnav">
        <router-link to="/" exact active-class="is-active">Início</router-link>
        <router-link
          :to="{ path: '/', query: { buscar: '1' } }"
          active-class="is-active"
        >
          Buscar profissionais de saúde
        </router-link>
        <router-link v-if="loggedIn" to="/consultas" active-class="is-active">
          Minhas consultas
        </router-link>
        <router-link v-if="loggedIn" to="/documentos" active-class="is-active">
          Documentos
        </router-link>
        <router-link
          v-if="loggedIn"
          to="/meus-pagamentos"
          active-class="is-active"
        >
          Pagamentos
        </router-link>
      </nav>
      <span class="appbar-spacer" />
      <AppBarUserMenu v-if="loggedIn" />
      <div v-else class="appbar-auth">
        <router-link to="/login" class="btn btn--outline btn--sm">
          Entrar
        </router-link>
        <router-link to="/cadastrar" class="btn btn--primary btn--sm">
          Criar conta
        </router-link>
      </div>
    </header>

    <!-- ═══ WIZARD ═══ -->
    <div class="wizard-row">
      <div class="steps">
        <div class="step step--done">
          <span class="step-dot">✓</span>
          <span class="step-label">Profissional</span>
        </div>
        <span class="step-bar step-bar--done" />
        <template v-if="!isExpressAppointment">
          <div class="step step--done">
            <span class="step-dot">✓</span>
            <span class="step-label">Horário</span>
          </div>
          <span class="step-bar step-bar--done" />
        </template>
        <div class="step step--active">
          <span class="step-dot">{{ isExpressAppointment ? "2" : "3" }}</span>
          <span class="step-label">Pagamento</span>
        </div>
        <span class="step-bar" />
        <div class="step">
          <span class="step-dot">{{ isExpressAppointment ? "3" : "4" }}</span>
          <span class="step-label">Confirmação</span>
        </div>
      </div>
    </div>

    <!-- ═══ CHECKOUT ═══ -->
    <div class="checkout">
      <div class="checkout-grid">
        <!-- PAGAMENTO -->
        <div class="card pay-card">
          <h2>Forma de pagamento</h2>

          <div class="pay-methods">
            <div
              class="pmethod"
              :class="{ on: selectedMethod === 'pix' }"
              @click="selectedMethod = 'pix'"
            >
              <span class="rdo" />
              <span class="pm-label">Pix</span>
              <span class="badge badge--success badge--no-dot">
                Aprovação na hora
              </span>
            </div>
            <div
              class="pmethod"
              :class="{ on: selectedMethod === 'credit_card' }"
              @click="selectedMethod = 'credit_card'"
            >
              <span class="rdo" />
              <span class="pm-label pm-label--muted">Cartão de crédito</span>
              <span class="pm-hint">em até 3x</span>
            </div>
          </div>

          <!-- PIX PANEL -->
          <div v-if="selectedMethod === 'pix'" class="pix-panel">
            <div
              v-if="loadingPayment"
              class="qr qr--loading"
              aria-label="Carregando QR Code"
            />
            <img
              v-else-if="pixQrCodeUrl"
              class="qr qr--img"
              :src="pixQrCodeUrl"
              alt="QR Code Pix"
            />
            <div v-else class="qr" aria-label="QR Code Pix" />
            <div>
              <h4>Pague com Pix para confirmar</h4>
              <p>
                Escaneie o QR Code no app do seu banco ou copie o código abaixo.
                A confirmação é automática.
              </p>
              <div v-if="loadingPayment" class="pix-code-loading">
                Gerando cobrança…
              </div>
              <div v-else-if="paymentError" class="pix-error">
                {{ paymentError }}
              </div>
              <div v-else class="pix-code">
                <input
                  class="input"
                  :value="pixCode"
                  readonly
                  aria-label="Código Pix"
                />
                <button class="btn btn--outline btn--sm" @click="copyPix">
                  {{ copied ? "Copiado!" : "Copiar" }}
                </button>
              </div>
            </div>
          </div>

          <!-- CARTÃO PANEL -->
          <div v-if="selectedMethod === 'credit_card'" class="card-panel">
            <div v-if="creditCards.length" class="card-list">
              <label
                v-for="card in creditCards"
                :key="card.id"
                class="pmethod"
                :class="{
                  on: selectedCreditCard && selectedCreditCard.id === card.id
                }"
              >
                <input
                  type="radio"
                  name="credit_card_radio"
                  :value="card"
                  v-model="selectedCreditCard"
                  style="position:absolute;opacity:0;width:0;height:0"
                />
                <span class="rdo" />
                <span class="pm-label">
                  {{ card.nickname }} •••• {{ card.last_four }}
                </span>
                <span class="pm-hint">{{ card.brand }}</span>
              </label>
            </div>

            <button
              class="btn btn--outline btn--sm card-add-btn"
              @click="openAddCardModal"
            >
              + Adicionar novo cartão
            </button>

            <div v-if="cardPaymentError" class="pix-error">
              {{ cardPaymentError }}
              <span class="card-error-hint">
                Possíveis causas: limite insuficiente, cartão bloqueado ou não
                habilitado para compras online. Tente com outro cartão ou entre
                em contato com seu banco.
              </span>
            </div>
          </div>

          <!-- MODAL: adicionar cartão -->
          <BModal
            id="createCreditCard"
            ref="createCreditCardModal"
            size="lg"
            centered
            hide-footer
          >
            <CreditCard @pushNewCreditCard="onCardAdded" />
          </BModal>

          <!-- MODAL: cadastrar endereço -->
          <BModal
            id="updateAddress"
            ref="updateAddressModal"
            size="lg"
            centered
            hide-footer
          >
            <AddressForm @addressCreated="onAddressCreated" />
          </BModal>

          <!-- STATUS -->
          <div class="pay-status">
            <span class="dot" />
            <span>Aguardando pagamento · a tela atualiza automaticamente</span>
          </div>
        </div>

        <!-- RESUMO -->
        <aside class="card summary-card">
          <div class="summary-doc">
            <div class="avatar avatar--md avatar--ring">
              <img
                v-if="doctor.photo"
                :src="doctor.photo"
                :alt="doctorName"
                loading="lazy"
                decoding="async"
              />
              <template v-else>{{ doctorInitials }}</template>
            </div>
            <div class="stack" style="gap:1px">
              <span class="dnm">{{ doctorName }}</span>
              <span class="dsub">{{ doctorSpecialty }} · {{ doctorCRM }}</span>
            </div>
          </div>

          <div class="stack" style="gap:var(--space-3)">
            <div class="summary-row">
              <span class="k">Data</span>
              <span class="v">{{ scheduledDate }}</span>
            </div>
            <div class="summary-row">
              <span class="k">Horário</span>
              <span class="v hl">{{ scheduledTime }}</span>
            </div>
            <div class="summary-row">
              <span class="k">Modalidade</span>
              <span class="v">Vídeo · 30 min</span>
            </div>
            <div class="summary-row">
              <span class="k">Consulta</span>
              <span class="v">{{ doctorPrice }}</span>
            </div>
          </div>

          <div class="summary-total">
            <span class="tl">Total</span>
            <span class="tv">{{ doctorPrice }}</span>
          </div>

          <!--
            "Já paguei" comentado: o polling (checkPaymentStatus, a cada 5s)
            já redireciona sozinho quando a Iugu confirma o pix. Além de
            redundante, o clique navegava para a confirmação mesmo com o
            pagamento ainda pendente, já que checkPaymentStatus só marca
            paymentError quando o pix expira.

          <button
            v-if="selectedMethod === 'pix'"
            class="btn btn--accent btn--lg btn--block"
            :disabled="isConfirming"
            @click="confirmPayment"
          >
            {{ isConfirming ? "Confirmando…" : "Já paguei" }}
          </button>
          -->

          <button
            v-if="selectedMethod === 'credit_card'"
            class="btn btn--accent btn--lg btn--block"
            :disabled="
              !selectedCreditCard || isProcessingCard || isCreatingAppointment
            "
            @click="createCardPayment"
          >
            {{ isProcessingCard ? "Processando…" : "Confirmar com cartão" }}
          </button>

          <div class="book-trust">
            <span class="t">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                />
              </svg>
              Pagamento seguro e criptografado
            </span>
            <span class="t">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Cancelamento grátis até 24h antes
            </span>
          </div>
        </aside>
      </div>
    </div>

    <!-- ═══ RODAPÉ ═══ -->
    <footer class="foot-mini">
      <span class="vm-mark">
        <img
          class="vm-logo vm-logo--sm"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </span>
      <span class="cpy">© 2026 Videomedi · CNPJ 00.000.000/0001-00</span>
      <span class="links">
        <a href="#">Privacidade</a>
        <a href="#">Termos de Uso</a>
        <a href="#">LGPD</a>
        <a href="#">Suporte</a>
      </span>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { intToBRL } from "./../utils/currency/convert";
import { format, parseISO } from "date-fns";
import AppBarUserMenu from "./../components/AppBarUserMenu.vue";
import CreditCard from "./../components/CreditCard.vue";
import AddressForm from "./../components/AddressForm.vue";
import { Appointments } from "./../services/appointments";
import { isImmediateConsultation } from "./../utils/appointments/immediateConsultation";

const STORAGE_KEY = "videomedi:pending_pix";

export default {
  name: "SchedulePaymentView",
  components: { AppBarUserMenu, CreditCard, AddressForm },

  data() {
    return {
      selectedMethod: "pix",
      isConfirming: false,
      copied: false,
      loadingPayment: false,
      paymentError: null,
      appointmentResponse: null,
      pollInterval: null,
      creditCards: [],
      selectedCreditCard: null,
      isProcessingCard: false,
      cardPaymentError: null,
      failedAppointmentId: null,
      isCreatingAppointment: false
    };
  },

  async mounted() {
    await this.loadCreditCards();
    await this.initPixIfNeeded();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    ...mapActions("appointments", [
      "scheduleAppointment",
      "fetchPendingPayment",
      "clearScheduleAppointment"
    ]),
    ...mapActions("user", ["fetchUserCreditCards"]),

    // ── inicialização ────────────────────────────────────────────
    async loadCreditCards() {
      try {
        const { data } = await this.fetchUserCreditCards();
        this.creditCards = data || [];
      } catch {
        this.creditCards = [];
      }
    },

    async initPixIfNeeded() {
      if (this.selectedMethod !== "pix") return;
      const saved = this.restoreFromSession();
      if (saved) {
        this.appointmentResponse = saved;
      } else {
        await this.createPixPayment();
        if (this.appointmentResponse) this.saveToSession();
      }
      if (this.appointmentResponse) this.startPolling();
    },

    // ── cartão ───────────────────────────────────────────────────
    openAddCardModal() {
      if (!this.getCurrentUser?.address?.id) {
        this.$bvModal.show("updateAddress");
      } else {
        this.$bvModal.show("createCreditCard");
      }
    },

    onCardAdded(card) {
      this.creditCards.push(card);
      this.selectedCreditCard = card;
      this.$bvModal.hide("createCreditCard");
    },

    onAddressCreated() {
      this.$bvModal.hide("updateAddress");
      this.$bvModal.show("createCreditCard");
    },

    async createCardPayment() {
      if (!this.selectedCreditCard || !this.currentSchedule) return;
      this.isProcessingCard = true;
      this.cardPaymentError = null;
      try {
        await this.executeCardPayment();
        // limpa a sessão do PIX pendente criada automaticamente ao montar —
        // sem isso, a próxima tentativa de agendamento reaproveita essa
        // consulta já paga em vez de criar uma nova (ver checkPaymentStatus,
        // que já faz o mesmo para o PIX quando detecta "já pago").
        this.clearSession();
        this.navigateToConfirmation();
      } catch (err) {
        const error = err?.response?.data;
        if (error?.appointment_id)
          this.failedAppointmentId = error.appointment_id;
        this.cardPaymentError =
          error?.errors?.details ||
          Object.values(error?.errors || {})[0]?.[0] ||
          "Pagamento recusado. Verifique os dados do cartão e tente novamente.";
      } finally {
        this.isProcessingCard = false;
      }
    },

    async executeCardPayment() {
      const appointmentId =
        this.failedAppointmentId || this.appointmentResponse?.id;

      if (appointmentId) {
        const { data } = await Appointments.payWithCreditCard(
          appointmentId,
          this.selectedCreditCard.id
        );
        this.appointmentResponse = {
          id: appointmentId,
          order: data.order
        };
        this.failedAppointmentId = null;
        return;
      }

      if (this.isExpressAppointment) {
        const { data } = await this.scheduleNewAppointment();
        this.appointmentResponse = data;
        return;
      }

      // Agendamento por data/hora: cria a consulta sem cobrar na mesma
      // chamada e cobra num passo separado, pelo mesmo endpoint do retry —
      // evita a corrida com a confirmação assíncrona da Iugu que gerava
      // "payment_already_paid" indevido (ver PaymentOrchestrator).
      const { data } = await this.scheduleNewAppointment(false);
      this.appointmentResponse = data;
      const { data: payData } = await Appointments.payWithCreditCard(
        data.id,
        this.selectedCreditCard.id
      );
      this.appointmentResponse.order = payData.order;
    },

    scheduleNewAppointment(withPayment = true) {
      const scheduledAt = this.currentSchedule.scheduled_at || "";
      const date = scheduledAt.split("T")[0];
      const time = scheduledAt.split("T")[1] || "00:00:00";
      const payload = {
        scheduled_at: `${date} ${time}`,
        scheduled_until: `${date} 00:00:00`,
        doctor_id:
          this.currentSchedule.doctor_id || this.currentSchedule.doctor?.id,
        slot_id: this.currentSchedule.slot_id
      };
      if (withPayment) {
        payload.payment_type = "credit_card";
        payload.credit_card_id = this.selectedCreditCard.id;
      }
      return this.scheduleAppointment(payload);
    },

    // ── sessionStorage ──────────────────────────────────────────
    saveToSession() {
      if (!this.appointmentResponse?.id) return;
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          id: this.appointmentResponse.id,
          order: this.appointmentResponse.order,
          slotId: this.currentSchedule?.slot_id,
          doctorId:
            this.currentSchedule?.doctor_id || this.currentSchedule?.doctor?.id
        })
      );
    },

    restoreFromSession() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const saved = JSON.parse(raw);

        const currentSlot = this.currentSchedule?.slot_id;
        if (currentSlot && saved.slotId && saved.slotId !== currentSlot) {
          sessionStorage.removeItem(STORAGE_KEY);
          return null;
        }

        // Agendamento expresso não tem slot_id (a checagem acima não se aplica),
        // então validamos por médico + expiração do Pix pra não reaproveitar
        // uma consulta de outro médico ou de uma tentativa antiga abandonada.
        const currentDoctorId =
          this.currentSchedule?.doctor_id || this.currentSchedule?.doctor?.id;
        if (
          currentDoctorId &&
          saved.doctorId &&
          saved.doctorId !== currentDoctorId
        ) {
          sessionStorage.removeItem(STORAGE_KEY);
          return null;
        }

        const expiresAt = saved.order?.expires_at;
        if (expiresAt && new Date(expiresAt) <= new Date()) {
          sessionStorage.removeItem(STORAGE_KEY);
          return null;
        }

        return saved;
      } catch {
        return null;
      }
    },

    clearSession() {
      sessionStorage.removeItem(STORAGE_KEY);
    },

    // ── polling ─────────────────────────────────────────────────
    startPolling() {
      this.stopPolling();
      this.pollInterval = setInterval(() => this.checkPaymentStatus(), 5000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    async checkPaymentStatus() {
      if (!this.appointmentResponse?.id) return;
      try {
        const res = await this.fetchPendingPayment(this.appointmentResponse.id);
        if (res?.data?.order) {
          this.$set(this.appointmentResponse, "order", {
            ...this.appointmentResponse.order,
            ...res.data.order
          });
          this.saveToSession();
        }
      } catch (err) {
        const errorType = err?.response?.data?.error_type;
        if (errorType === "payment_already_paid") {
          this.stopPolling();
          this.clearSession();
          this.navigateToConfirmation();
        } else if (errorType === "payment_expired") {
          this.stopPolling();
          this.clearSession();
          this.paymentError =
            "Este código PIX expirou. Volte e selecione um novo horário.";
        }
      }
    },

    // ── criação ─────────────────────────────────────────────────
    async createPixPayment() {
      if (!this.currentSchedule || this.isCreatingAppointment) return;
      this.isCreatingAppointment = true;
      this.loadingPayment = true;
      this.paymentError = null;
      try {
        const scheduledAt = this.currentSchedule.scheduled_at || "";
        const [datePart, timePart] = scheduledAt.split("T");
        const slotDate = datePart || "";
        const slotHour = (timePart || "00:00:00").substring(0, 8);

        const payload = {
          scheduled_at: `${slotDate} ${slotHour}`,
          scheduled_until: `${slotDate} 00:00:00`,
          doctor_id:
            this.currentSchedule.doctor_id || this.currentSchedule.doctor?.id,
          slot_id: this.currentSchedule.slot_id,
          payment_type: "pix"
        };

        this.appointmentResponse = (
          await this.scheduleAppointment(payload)
        ).data;
      } catch (err) {
        const backendError =
          err?.response?.data?.errors?.base?.[0] || err?.response?.data?.error;
        this.paymentError =
          backendError || "Não foi possível gerar a cobrança. Tente novamente.";
      } finally {
        this.loadingPayment = false;
        this.isCreatingAppointment = false;
      }
    },

    // ── ações do usuário ─────────────────────────────────────────
    copyPix() {
      navigator.clipboard.writeText(this.pixCode).catch(() => {});
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    },

    async confirmPayment() {
      this.isConfirming = true;
      await this.checkPaymentStatus();
      this.isConfirming = false;
      if (!this.paymentError) this.navigateToConfirmation();
    },

    navigateToConfirmation() {
      this.$router.push({
        name: "Confirmation",
        query: { immediate: true },
        params: {
          payment_method: this.selectedMethod,
          appointmentResponse: this.appointmentResponse
        }
      });
    }
  },

  computed: {
    ...mapGetters("appointments", ["currentSchedule"]),
    ...mapGetters("auth", ["loggedIn"]),
    ...mapGetters("user", ["getCurrentUser"]),

    isExpressAppointment() {
      return isImmediateConsultation(this.currentSchedule);
    },
    doctor() {
      return this.currentSchedule?.doctor || {};
    },
    doctorInitials() {
      return (
        (this.doctor.name || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || ""
      );
    },
    doctorName() {
      return this.doctor.name || "—";
    },
    doctorSpecialty() {
      return this.doctor.doctor_property?.medical_specialty_name || "—";
    },
    doctorCRM() {
      return this.doctor.doctor_property?.council_description || "—";
    },
    doctorPrice() {
      const price =
        this.doctor.doctor_property?.price_cents_and_earning_margin_sum ||
        this.doctor.doctor_property?.price_cents;
      return price ? intToBRL(price) : "—";
    },
    scheduledDate() {
      const dt = this.currentSchedule?.scheduled_at;
      if (!dt) return "—";
      try {
        const d = new Date(dt);
        return d
          .toLocaleDateString("pt-BR", {
            weekday: "short",
            day: "numeric",
            month: "short"
          })
          .replace(/\.\s*/g, " ")
          .trim()
          .replace(/^\w/, c => c.toUpperCase());
      } catch {
        return "—";
      }
    },
    scheduledTime() {
      const dt = this.currentSchedule?.scheduled_at;
      if (!dt) return "—";
      try {
        return new Date(dt).toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit"
        });
      } catch {
        return "—";
      }
    },
    pixQrCodeUrl() {
      return this.appointmentResponse?.order?.pix_data?.qrcode || null;
    },
    pixCode() {
      return this.appointmentResponse?.order?.pix_data?.qrcode_text || "";
    }
  },

  watch: {
    selectedMethod(newMethod) {
      if (newMethod === "pix") {
        this.cardPaymentError = null;
        this.initPixIfNeeded();
      } else {
        this.stopPolling();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.vm-logo {
  display: block;
  height: 28px;
  width: auto;
}

.vm-logo--sm {
  height: 22px;
}

/* ─── PAGE ─── */
.pay-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--color-bg-page, #f4f6fb);
}

/* ─── APPBAR ─── */
.appbar {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  background: #fff;
  border-bottom: 1px solid var(--color-gray-200);
  padding: 0 var(--space-8);
  height: 68px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.vm-mark {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  text-decoration: none;
}

.vm-wordmark {
  font-size: var(--font-size-md);
  font-weight: var(--fw-extra);
  color: var(--color-accent);
  letter-spacing: -0.01em;
}

.appnav {
  display: flex;
  align-items: center;
  gap: var(--space-6);

  a {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-600);
    text-decoration: none;
    cursor: pointer;

    &:hover {
      color: var(--color-primary);
    }
    &.is-active {
      color: var(--color-accent);
    }
    &.router-link-exact-active {
      color: var(--color-primary);
    }
  }
}

.appbar-spacer {
  flex: 1;
}

.appbar-auth {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.appbar-user {
  display: flex;
  align-items: center;
  gap: var(--space-2);

  .nm {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-bold);
    color: var(--color-gray-800);
  }
}

/* ─── WIZARD ─── */
.wizard-row {
  display: flex;
  justify-content: center;
  padding: var(--space-6) var(--space-10) 0;
}

.steps {
  display: flex;
  align-items: center;
  max-width: 620px;
  width: 100%;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);

  .step-dot {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid var(--color-gray-300);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--font-size-xs);
    font-weight: var(--fw-bold);
    color: var(--color-gray-400);
    background: #fff;
  }

  .step-label {
    font-size: var(--font-size-xs);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-400);
    white-space: nowrap;
  }

  &.step--done .step-dot {
    background: var(--color-success);
    border-color: var(--color-success);
    color: #fff;
  }
  &.step--done .step-label {
    color: var(--color-gray-700);
  }
  &.step--active .step-dot {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }
  &.step--active .step-label {
    color: var(--color-primary);
    font-weight: var(--fw-bold);
  }
}

.step-bar {
  flex: 1;
  height: 2px;
  background: var(--color-gray-200);
  margin: 0 var(--space-1);
  margin-top: -16px;

  &.step-bar--done {
    background: var(--color-success);
  }
}

/* ─── CHECKOUT ─── */
.checkout {
  max-width: 960px;
  margin: 0 auto;
  padding: var(--space-8) var(--space-10) var(--space-12);
  width: 100%;

  @media (max-width: 860px) {
    padding: var(--space-5);
  }
}

.checkout-grid {
  display: grid;
  grid-template-columns: 1fr 348px;
  gap: var(--space-8);
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
}

/* ─── CARD: padding ausente no global do app ─── */
.card {
  padding: var(--space-6);
}

/* ─── PAY CARD ─── */
.pay-card {
  padding: var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);

  h2 {
    font-size: var(--font-size-lg);
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
    margin: 0;
  }
}

/* ─── PAY METHODS ─── */
.pay-methods {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.pmethod {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  border: 1.5px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;

  &:hover {
    border-color: var(--color-primary);
  }

  &.on {
    border-color: var(--color-primary);
    background: var(--color-primary-light);

    .rdo {
      border-color: var(--color-primary);
      background: radial-gradient(circle, var(--color-primary) 40%, #fff 45%);
    }
  }
}

.rdo {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid var(--color-gray-300);
  flex: none;
  transition: border-color 0.12s, background 0.12s;
}

.pm-label {
  flex: 1;
  font-size: var(--font-size-sm);
  font-weight: var(--fw-bold);
  color: var(--color-gray-900);

  &.pm-label--muted {
    font-weight: var(--fw-semibold);
    color: var(--color-gray-600);
  }
}

.pm-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
}

/* ─── PIX PANEL ─── */
.pix-panel {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--space-6);
  align-items: center;
  padding: var(--space-6);
  background: var(--color-gray-50, #f8fafc);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);

  h4 {
    font-size: var(--font-size-md);
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
    margin: 0 0 var(--space-1);
  }

  p {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);
    margin: 0 0 var(--space-3);
    line-height: 1.55;
  }
}

/* QR code visual — padrão do handoff */
.qr {
  width: 168px;
  height: 168px;
  flex: none;
  border-radius: var(--radius-md);
  background: repeating-conic-gradient(#0f172a 0% 25%, #fff 0% 50%) 50% / 14px
    14px;
  border: 6px solid #fff;
  box-shadow: var(--shadow-card);

  &--img {
    background: #fff;
    object-fit: contain;
    padding: 4px;
  }

  &--loading {
    opacity: 0.3;
    animation: pulse 1.2s ease-in-out infinite;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.6;
  }
}

.pix-code-loading {
  font-size: var(--font-size-sm);
  color: var(--color-gray-400);
  padding: var(--space-2) 0;
}

.pix-error {
  font-size: var(--font-size-sm);
  color: var(--color-danger, #dc2626);
  padding: var(--space-2) 0;
}

.card-error-hint {
  display: block;
  margin-top: var(--space-1);
  font-size: var(--font-size-sm);
  color: var(--color-text-muted, #6c757d);
}

/* ─── PIX CODE ─── */
.pix-code {
  display: flex;
  gap: var(--space-2);

  .input {
    flex: 1;
    border: 1.5px solid var(--color-gray-200);
    border-radius: var(--radius-md);
    height: 42px;
    padding: 0 var(--space-3);
    font-family: var(--font-num, "Inter", sans-serif);
    font-size: var(--font-size-xs);
    color: var(--color-gray-700);
    background: #fff;
    outline: none;
  }
}

/* ─── PAY STATUS ─── */
.pay-status {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: var(--color-warning-light, #fef9c3);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-xs);
  color: var(--color-warning, #ca8a04);
  font-weight: var(--fw-semibold);

  .dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--color-warning, #ca8a04);
    flex: none;
  }
}

/* ─── SUMMARY CARD ─── */
.summary-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);

  @media (max-width: 860px) {
    position: static;
  }
}

.summary-doc {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--color-gray-100);

  .dnm {
    display: block;
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
    font-size: var(--font-size-sm);
  }
  .dsub {
    display: block;
    font-size: var(--font-size-xs);
    color: var(--color-gray-500);
  }
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-sm);

  .k {
    color: var(--color-gray-500);
  }
  .v {
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
  }
  .v.hl {
    color: var(--color-success);
  }
}

.summary-total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);

  .tl {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);
    font-weight: var(--fw-semibold);
  }
  .tv {
    font-family: var(--font-num, "Inter", sans-serif);
    font-size: var(--font-size-2xl);
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
  }
}

/* ─── BOOK TRUST ─── */
.book-trust {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-gray-100);

  .t {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-xs);
    color: var(--color-gray-500);

    svg {
      color: var(--color-success);
      flex: none;
    }
  }
}

/* ─── CARD PANEL ─── */
.card-panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5);
  background: var(--color-gray-50, #f8fafc);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.card-add-btn {
  align-self: flex-start;
}

/* ─── FOOT MINI ─── */
.foot-mini {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-5) var(--space-8);
  border-top: 1px solid var(--color-gray-200);
  background: var(--color-bg-page, #f4f6fb);
  margin-top: auto;

  .vm-mark {
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }
  .vm-wordmark {
    color: var(--color-gray-500);
    font-size: var(--font-size-sm);
    font-weight: normal;
  }
  .cpy {
    font-size: var(--font-size-xs);
    color: var(--color-gray-400);
  }
  .links {
    margin-left: auto;
    display: flex;
    gap: var(--space-5);

    a {
      font-size: var(--font-size-xs);
      font-weight: var(--fw-semibold);
      color: var(--color-gray-500);
      text-decoration: none;
      &:hover {
        color: var(--color-primary);
      }
    }
  }
}
</style>
